import {
  KoButton,
  KoDrawer,
  KoForm,
  KoLoader,
  KoModal,
  KoTable,
  KoText,
  KoTitle,
  KoTag,
  KoToast,
  KoEmpty,
  KoPagination,
  KoSelect
} from 'packages';
import KoIcon from '@kwapio/icons';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { add, remove } from 'shared/store/breadcrumbSlice';
import KoToolBar from 'shared/components/toolbar';
import { ApiPlatformAPI } from '../services/apiplatform.service';
import { COLUMNS_COLLECTIONS, HTTP_METHOD_CONFIG, TEST_RESULT_CONFIG } from '../constants';
import CollectionCrud from './crud/CollectionCrud';
import ApiSubNav from './ApiSubNav';
import styles from './apiplatform.module.scss';

const MOCK_COLLECTIONS = [
  { _id: 'col-001', name: 'User Auth Flow', description: 'End-to-end user registration and login', endpointsCount: 6, version: 'v1.2', owner: 'Auth Team', lastRun: '2026-02-24T10:30:00Z', updatedAt: '2026-02-24T10:30:00Z' },
  { _id: 'col-002', name: 'Payment Processing', description: 'Charge, refund, and subscription lifecycle', endpointsCount: 8, version: 'v2.0', owner: 'Billing Team', lastRun: '2026-02-24T09:15:00Z', updatedAt: '2026-02-24T09:15:00Z' },
  { _id: 'col-003', name: 'Search & Filter', description: 'Full-text search with pagination and facets', endpointsCount: 4, version: 'v1.0', owner: 'Data Team', lastRun: '2026-02-23T18:00:00Z', updatedAt: '2026-02-23T18:00:00Z' },
  { _id: 'col-004', name: 'File Upload Suite', description: 'Upload, download, and CDN verification', endpointsCount: 5, version: 'v1.1', owner: 'Platform Team', lastRun: '2026-02-24T08:00:00Z', updatedAt: '2026-02-24T08:00:00Z' },
  { _id: 'col-005', name: 'Notification Delivery', description: 'Email, push, and SMS channel testing', endpointsCount: 7, version: 'v1.3', owner: 'Platform Team', lastRun: '2026-02-23T14:45:00Z', updatedAt: '2026-02-23T14:45:00Z' }
];

const MOCK_COL_SERVICE_LIST = [
  { label: 'User Service', value: 'svc-001' },
  { label: 'Payment Gateway', value: 'svc-002' },
  { label: 'Notification Hub', value: 'svc-003' },
  { label: 'Search Engine', value: 'svc-004' },
  { label: 'File Storage', value: 'svc-006' }
];

const MOCK_ENV_LIST = [
  { label: 'Development', value: 'env-001' },
  { label: 'Staging', value: 'env-002' },
  { label: 'Production', value: 'env-003' }
];

const MOCK_RUN_HISTORY = [
  { runId: 'run-001', timestamp: '2026-02-24T10:30:00Z', environment: 'Staging', duration: '4.2s', passed: 6, failed: 0, skipped: 0, status: 'passed' },
  { runId: 'run-002', timestamp: '2026-02-24T08:15:00Z', environment: 'Development', duration: '3.8s', passed: 5, failed: 1, skipped: 0, status: 'failed' },
  { runId: 'run-003', timestamp: '2026-02-23T16:00:00Z', environment: 'Staging', duration: '4.5s', passed: 6, failed: 0, skipped: 0, status: 'passed' }
];

function Collections() {
  const dispatch = useDispatch();
  const { workspaceId } = useParams();

  const [collections, setCollections] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ size: 20, page: 1 });

  const [isModalOpen, setModalOpen] = useState(false);
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);

  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [collectionEndpoints, setCollectionEndpoints] = useState([]);
  const [runHistory, setRunHistory] = useState([]);

  const [serviceList, setServiceList] = useState([]);
  const [endpointList ] = useState([]);
  const [environmentList, setEnvironmentList] = useState([]);
  const [selectedEnv, setSelectedEnv] = useState(null);
  const [form] = KoForm.useForm();

  const basePath = workspaceId
    ? `/workspace/${workspaceId}/development/apiplatform`
    : '/apiplatform';

  useEffect(() => {
    dispatch(remove());
    dispatch(add([
      { title: 'Home', path: 'home/myoverview' },
      { title: 'API Platform', path: basePath },
      { title: 'Collections' }
    ]));
    fetchLists();
  }, [workspaceId]);

  useEffect(() => {
    fetchCollections();
  }, [workspaceId, pagination]);

  const fetchLists = () => {
    ApiPlatformAPI.getServiceList().then(
      (data) => {
        const list = (data?.data || data || []).map(s => ({ label: s.name, value: s._id || s.serviceId }));
        setServiceList(list.length ? list : MOCK_COL_SERVICE_LIST);
      },
      () => setServiceList(MOCK_COL_SERVICE_LIST)
    );
    ApiPlatformAPI.getEnvironmentList().then(
      (data) => {
        const list = (data?.data || data || []).map(e => ({ label: e.name, value: e._id || e.environmentId }));
        setEnvironmentList(list.length ? list : MOCK_ENV_LIST);
      },
      () => setEnvironmentList(MOCK_ENV_LIST)
    );
  };

  const fetchCollections = () => {
    setLoading(true);
    ApiPlatformAPI.getCollections({ page: pagination.page, limit: pagination.size }).then(
      (res) => {
        const data = res?.data?.length ? res.data : MOCK_COLLECTIONS;
        setCollections(data);
        setTotalCount(res?.totalCount || data.length);
        setLoading(false);
      },
      () => { setCollections(MOCK_COLLECTIONS); setTotalCount(MOCK_COLLECTIONS.length); setLoading(false); }
    );
  };

  const handleSave = (values) => {
    setLoading(true);
    ApiPlatformAPI.createCollection(values, currentRecord?._id).then(
      () => {
        setLoading(false);
        setModalOpen(false);
        form.resetFields();
        setCurrentRecord(null);
        fetchCollections();
        KoToast.success(currentRecord ? 'Collection updated.' : 'Collection created.');
      },
      () => { setLoading(false); KoToast.error('Failed to save collection.'); }
    );
  };

  const handleDelete = () => {
    setLoading(true);
    ApiPlatformAPI.deleteCollection(currentRecord._id || currentRecord.collectionId).then(
      () => {
        setLoading(false);
        setDeleteOpen(false);
        setCurrentRecord(null);
        fetchCollections();
        KoToast.success('Collection deleted.');
      },
      () => { setLoading(false); KoToast.error('Delete failed.'); }
    );
  };

  const openDetail = (record) => {
    setSelectedCollection(record);
    setDrawerOpen(true);
    setCollectionEndpoints(record.endpoints || []);
    ApiPlatformAPI.getCollectionRuns(record._id || record.collectionId, { limit: 5 }).then(
      (res) => { const data = res?.data?.length ? res.data : MOCK_RUN_HISTORY; setRunHistory(data); },
      () => setRunHistory(MOCK_RUN_HISTORY)
    );
  };

  const handleRunCollection = () => {
    if (!selectedCollection) return;
    setLoading(true);
    ApiPlatformAPI.runCollection(selectedCollection._id || selectedCollection.collectionId, selectedEnv).then(
      () => {
        setLoading(false);
        KoToast.success('Collection run started.');
        ApiPlatformAPI.getCollectionRuns(selectedCollection._id || selectedCollection.collectionId, { limit: 5 }).then(
          (res) => setRunHistory(res?.data || res || []),
          () => {}
        );
      },
      () => { setLoading(false); KoToast.error('Failed to run collection.'); }
    );
  };

  const endpointMiniColumns = [
    { label: 'Method', id: 'method', key: 'method', width: 80,
      render: (val) => {
        const cfg = HTTP_METHOD_CONFIG[val] || {};
        return <KoTag color={cfg.color}>{cfg.label || val}</KoTag>;
      }
    },
    { label: 'Path', id: 'path', key: 'path', width: 300,
      render: (val) => <span style={{ fontFamily: 'monospace', fontSize: '1.2rem' }}>{val || '—'}</span>
    },
    { label: 'Summary', id: 'summary', key: 'summary', width: 200 }
  ];

  const columnsWithActions = [
    ...COLUMNS_COLLECTIONS,
    {
      label: '', id: 'actions', key: 'actions', width: 120,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '4px' }}>
          <KoButton
            iconBtn
            icon={<KoIcon name='EyeOutlined' width={14} height={14} />}
            onClick={(e) => { e.stopPropagation(); openDetail(record); }}
          />
          <KoButton
            iconBtn
            icon={<KoIcon name='EditOutlined' width={14} height={14} />}
            onClick={(e) => { e.stopPropagation(); setCurrentRecord(record); form.setFieldsValue(record); setModalOpen(true); }}
          />
          <KoButton
            iconBtn
            danger
            icon={<KoIcon name='DeleteOutlined' width={14} height={14} />}
            onClick={(e) => { e.stopPropagation(); setCurrentRecord(record); setDeleteOpen(true); }}
          />
        </div>
      )
    }
  ];

  return (
    <div className='tableCard'>
      {isLoading && <KoLoader />}
      <ApiSubNav activeKey='collections' />

      <KoToolBar
        title='Collections'
        search={{ visible: true, placeholder: 'Search collections...', onChange: () => {} }}
        filter={{ visible: false, options: [] }}
        buttonList={[{
          label: 'New Collection',
          type: 'primary',
          icon: <KoIcon color='#FFF' name='PlusOutlined' width={18} height={18} />,
          onClick: () => { setCurrentRecord(null); form.resetFields(); setModalOpen(true); }
        }]}
      />

      {collections.length === 0 && !isLoading && <KoEmpty description='No collections found' />}
      {collections.length > 0 && (
        <>
          <KoTable maxHeight={480} dataSource={collections} columns={columnsWithActions} rowKey={(r) => r._id || r.collectionId} />
          <KoPagination totalCount={totalCount} pageSize={pagination.size} currentPage={pagination.page} onChange={(page, size) => setPagination({ page, size })} />
        </>
      )}

      {/* Collection Detail Drawer */}
      <KoDrawer
        title='Collection Detail'
        open={isDrawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedCollection(null); setRunHistory([]); setSelectedEnv(null); }}
        width={'min(700px, 95vw)'}
      >
        {selectedCollection && (
          <div>
            <div className={styles.drawerHeader}>
              <KoTitle level={4} text={selectedCollection.name} />
              {selectedCollection.version && <KoTag>{selectedCollection.version}</KoTag>}
            </div>

            {selectedCollection.description && (
              <div className={styles.detailSection}>
                <div className={styles.detailLabel}>Description</div>
                <KoText text={selectedCollection.description} />
              </div>
            )}

            <div className={styles.detailSection}>
              <div className={styles.detailLabel}>Endpoints ({collectionEndpoints.length})</div>
              {collectionEndpoints.length > 0 ? (
                <KoTable dataSource={collectionEndpoints} columns={endpointMiniColumns} rowKey={(r) => r._id || r.endpointId} maxHeight={240} />
              ) : (
                <KoText text='No endpoints in this collection' />
              )}
            </div>

            <div className={styles.detailSection}>
              <div className={styles.detailLabel}>Run Collection</div>
              <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center', marginTop: '0.8rem' }}>
                <KoSelect
                  style={{ minWidth: 200 }}
                  value={selectedEnv}
                  onChange={setSelectedEnv}
                  placeholder='Select environment'
                  options={environmentList}
                  allowClear
                />
                <KoButton type='primary' label='Run All' onClick={handleRunCollection} icon={<KoIcon name='PlayCircleOutlined' width={14} height={14} color='#FFF' />} />
              </div>
            </div>

            <div className={styles.detailSection}>
              <div className={styles.detailLabel}>Run History (Last 5)</div>
              {runHistory.length === 0 && <KoText text='No run history' />}
              {runHistory.map((run, i) => (
                <div key={i} className={styles.runHistoryItem}>
                  <span className={styles.runHistoryDate}>{run.timestamp || run.createdAt || '—'}</span>
                  <div className={styles.runHistoryStats}>
                    <span style={{ color: '#52c41a' }}>{run.passed || 0} passed</span>
                    <span style={{ color: '#f5222d' }}>{run.failed || 0} failed</span>
                    <span style={{ color: '#8c8c8c' }}>{run.skipped || 0} skipped</span>
                  </div>
                  <KoTag color={TEST_RESULT_CONFIG[run.status]?.color}>{TEST_RESULT_CONFIG[run.status]?.label || run.status}</KoTag>
                </div>
              ))}
            </div>
          </div>
        )}
      </KoDrawer>

      {/* Create/Edit Modal */}
      <KoModal
        title={currentRecord ? 'Edit Collection' : 'New Collection'}
        centered
        open={isModalOpen}
        handleOk={() => setModalOpen(false)}
        onClose={() => { setModalOpen(false); setCurrentRecord(null); }}
        width={'min(700px, 95vw)'}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => { setModalOpen(false); setCurrentRecord(null); }} />,
          <KoButton key='save' type='primary' loading={isLoading} label='Save' onClick={() => form.submit()} />
        ]}
      >
        <CollectionCrud form={form} handleFinish={handleSave} services={serviceList} endpoints={endpointList} />
      </KoModal>

      {/* Delete Confirmation */}
      <KoModal
        title='Delete Collection'
        centered
        open={isDeleteOpen}
        handleOk={() => setDeleteOpen(false)}
        onClose={() => { setDeleteOpen(false); setCurrentRecord(null); }}
        width={'min(440px, 95vw)'}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => { setDeleteOpen(false); setCurrentRecord(null); }} />,
          <KoButton key='delete' type='primary' danger loading={isLoading} label='Delete' onClick={handleDelete} />
        ]}
      >
        <div className={styles.confirmBody}>
          <KoTitle level={5} text={`Are you sure you want to delete "${currentRecord?.name}"?`} />
          <KoText text='This action cannot be undone.' />
        </div>
      </KoModal>
    </div>
  );
}

export default Collections;
